/** @format */

import type { ReactNode } from 'react';
import { cn } from '@clarix/ui';

interface PageShellProps {
  children: ReactNode;
  className?: string;
  /**
   * Cancels the shell `<main>`'s top `pt-4` gap with `-mt-4`. Defaults to
   * `true` so a page's first bar (InformativeBar / ActionBar / SwitcherBar)
   * lines up exactly with the sidebar panel header (`h-11`, also flush under
   * the TopBar) — they share the same top edge. Set `bleed={false}` only for a
   * page that genuinely wants the 16px breathing gap above its content.
   * The shell's <main> is already `flex min-h-0 flex-1`, so `h-full` fills it
   * exactly — no viewport `calc()` is ever needed.
   */
  bleed?: boolean;
}

/**
 * The single page-height container for every projects page.
 *
 * Replaces the three divergent height models the app grew organically:
 *   - `flex h-full min-h-0 w-full flex-col`        (list / reports — correct)
 *   - `h-[calc(100vh-64px)] -mt-4`                 (detail — hardcoded hack)
 *   - `-my-4 … style={{ height: 'calc(100% + 32px)' }}` (calendar — inline style)
 *
 * Compose it with InformativeBar / SwitcherBar / ActionBar (fixed-height,
 * `shrink-0` rows) followed by a single `PageShell.Body` that flexes to fill
 * and scrolls. This guarantees identical page height + bar rhythm everywhere.
 */
export function PageShell({ children, className, bleed = true }: PageShellProps) {
  return (
    <div
      className={cn(
        'flex min-h-0 w-full flex-col',
        // `bleed` pulls the page up 16px so its first bar lines up with the
        // sidebar panel header. Grow the height by the SAME 16px (calc) so the
        // page still reaches the bottom edge — otherwise it ends 16px short,
        // leaving a blank strip under a table's pagination footer.
        bleed ? 'h-[calc(100%+1rem)] -mt-4' : 'h-full',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface PageShellBodyProps {
  children: ReactNode;
  className?: string;
  /** Add the standard `p-5` content padding. Off by default so tables/grids own their edges. */
  padded?: boolean;
  /** Scroll overflow vertically (default). Turn off for panes that manage their own scroll. */
  scroll?: boolean;
}

/**
 * The scrollable content region below the bars. Always `flex-1 min-h-0` so it
 * consumes exactly the remaining height under the fixed bars.
 */
export function PageShellBody({
  children,
  className,
  padded = false,
  scroll = true,
}: PageShellBodyProps) {
  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col',
        scroll && 'overflow-y-auto',
        padded && 'p-5',
        className,
      )}
    >
      {children}
    </div>
  );
}

PageShell.Body = PageShellBody;
